import { ref } from 'vue'
import { useAuth } from './useAuth'

// Constants for timeout durations (in milliseconds)
const WARNING_TIMEOUT = 50 * 60 * 1000 // 30 minutes
const LOGOUT_TIMEOUT = 1 * 60 * 60 * 1000 // 1 hour

export function useInactivityTracker() {
  const { logout } = useAuth()
  const showWarning = ref(false)
  const warningTimer = ref<number | null>(null)
  const logoutTimer = ref<number | null>(null)

  // Reset timers when user activity is detected
  const resetTimers = () => {
    // Clear existing timers
    if (warningTimer.value) {
      clearTimeout(warningTimer.value)
      warningTimer.value = null
    }

    if (logoutTimer.value) {
      clearTimeout(logoutTimer.value)
      logoutTimer.value = null
    }

    // Hide warning if it's showing
    showWarning.value = false

    // Set new timers
    warningTimer.value = window.setTimeout(() => {
      showWarning.value = true
    }, WARNING_TIMEOUT)

    logoutTimer.value = window.setTimeout(async () => {
      await logout()
      window.location.href = '/auth/login'
    }, LOGOUT_TIMEOUT)
  }

  // Start tracking user activity
  const startTracking = () => {
    // Disable inactivity tracking when running the dev server.
    // This makes local development less annoying and avoids unexpected log-outs.
    if (import.meta.env.DEV) {
      return () => {}
    }

    // User activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ]

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, resetTimers)
    })

    // Initial timer setup
    resetTimers()

    // Cleanup function
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimers)
      })

      if (warningTimer.value) clearTimeout(warningTimer.value)
      if (logoutTimer.value) clearTimeout(logoutTimer.value)
    }
  }

  // Manually dismiss the warning and reset timers
  const dismissWarning = () => {
    showWarning.value = false
    resetTimers()
  }

  return {
    showWarning,
    startTracking,
    dismissWarning,
  }
}
